import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

const useAppAnalysisDetailsLogic = (fileId) => {
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAppDetails = async () => {
      if (!fileId) {
        setError('No file ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First attempt: Direct lookup by fileId
        const apkAnalysisRef = doc(db, 'apk_analysis', fileId);
        const apkAnalysisSnap = await getDoc(apkAnalysisRef);
        
        if (apkAnalysisSnap.exists()) {
          const analysisData = apkAnalysisSnap.data();
          console.log('Found document directly by ID');
          setAppDetails(analysisData);
          setLoading(false);
          return;
        }

        console.log('Document not found by direct ID, trying alternatives...');
        
        // Second attempt: Check if fileId equals the file_id field in collection
        const filesCollection = collection(db, 'apk_analysis');
        const q = query(filesCollection, where('file_id', '==', fileId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Take the first matching document
          const doc = querySnapshot.docs[0];
          const analysisData = doc.data();
          console.log(`Found by file_id field match: ${doc.id}`);
          setAppDetails(analysisData);
          setLoading(false);
          return;
        }

        // Additional attempt: Check if fileId matches part of file_name field
        // Try both .aab and .apk extensions for backwards compatibility
        const q4 = query(filesCollection, where('file_name', '==', `${fileId}.aab`));
        const querySnapshot4 = await getDocs(q4);
        
        if (!querySnapshot4.empty) {
          const doc = querySnapshot4.docs[0];
          const analysisData = doc.data();
          console.log(`Found by file_name field match: ${doc.id}`);
          setAppDetails(analysisData);
          setLoading(false);
          return;
        }
        
        // Third attempt: Partial search if fileId is part of a longer identifier
        try {
          const fileBaseName = fileId.split('_')[0] + '_' + fileId.split('_')[1];
          const q2 = query(filesCollection, where('file_id', '>=', fileBaseName), where('file_id', '<=', fileBaseName + '\uf8ff'));
          const querySnapshot2 = await getDocs(q2);
          
          if (!querySnapshot2.empty) {
            const doc = querySnapshot2.docs[0];
            const analysisData = doc.data();
            console.log(`Found by partial file_id match: ${doc.id}`);
            setAppDetails(analysisData);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.log("Error in partial search:", error);
          // Continue with next attempt
        }
        
        // Check userApps collection for additional information
        console.log("Checking userApps collection for more information");
        const userAppsCollection = collection(db, 'userApps');
        const q3 = query(userAppsCollection, where('fileId', '==', fileId));
        const userAppSnapshot = await getDocs(q3);
        
        if (!userAppSnapshot.empty) {
          // File exists in userApps, but analysis doesn't exist or has a different ID
          const userAppData = userAppSnapshot.docs[0].data();
          console.log(`Found in userApps: status=${userAppData.status}, but not in apk_analysis`);
          
          // If the application is still being analyzed, display appropriate message
          if (userAppData.status === 'analyzing') {
            setError(`Analysis is still in progress. Please check back later.`);
          } else if (userAppData.status === 'uploaded') {
            setError(`Analysis has not started yet. Please check back later or try triggering the analysis again.`);
          } else {
            setError(`Analysis data is not available for this application. Status: ${userAppData.status || 'unknown'}. Please wait for analysis to complete or contact support.`);
          }
        } else {
          console.log(`Application with fileId ${fileId} not found in any collection`);
          setError('Analysis not found for this application. The file may have been deleted or the ID is incorrect.');
        }

      } catch (error) {
        console.error('Error fetching app details:', error);
        setError(`Failed to load application details: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAppDetails();
  }, [fileId]);

  return {
    appDetails,
    loading,
    error
  };
};

export default useAppAnalysisDetailsLogic; 